import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '@beyond-js/ui/react';
import { Session as Renewal } from '@beyond-js/ui/dom';
import { Session, type Identity } from '@snapshots/domain';
import type { Language } from '@snapshots/locale';
import { Refusal, type Client } from '@snapshots/transport';

/**
 * The renewals of the session a page has seen: views read what they show again after one (`count`),
 * and a list waiting for one resumes at the next (`next()`)
 */
export class Renewals {
	#count = 0;
	#waiting: (() => void)[] = [];

	get count(): number {
		return this.#count;
	}

	/**
	 * Who did not answer a session's read, as `Session` names it by id (`CauseName`): the authority the service
	 * said did not answer it, or else the service itself
	 */
	static source(refusal: Refusal): string {
		const dependency = String(refusal.details?.dependency ?? '');
		return ['accounts', 'projects'].includes(dependency) ? dependency : 'snapshots';
	}

	/** Resolves at the next renewal */
	next(): Promise<void> {
		return new Promise(resolve => this.#waiting.push(resolve));
	}

	notify(): void {
		this.#count += 1;
		const waiting = this.#waiting;
		this.#waiting = [];
		for (const resolve of waiting) resolve();
	}
}

interface Options {
	client: Client;
	session: Session;
	/** Who is signed in; `Session` is created once it is known and `ready` */
	identity: Identity | undefined;
	/** Whether what `accounts` depends on has been read (the bar's descriptor, settled either way) */
	ready: boolean;
	/** Beyond Accounts' account page, offered to a suspended account */
	accounts: string | null;
	language: Language;
	/** "Use another account": the family bar's "Sign out of Beyond" */
	other: () => void;
	/** Takes the identity a renewal read */
	adopt: (identity: Identity) => void;
}

/**
 * The family's one answer to a session that ends while the person works (`Session` of
 * `@beyond-js/ui`, through `useSession`): renewed in a hidden frame with nothing shown, or one dialog
 * that cannot be dismissed when the person is needed, so the page is never open without a session. It
 * reads the session with this service's `GET /v1/session`, starts the hand-off with `mode`, and becomes
 * the transport's `guard`, so every request that comes back without a session waits behind it. A
 * renewal adopts the identity it read and counts in `renewed`, which views use to read again; someone
 * else signing in starts the application again at its entry, dropping what the previous person typed.
 */
export function useRenewal({ client, session, identity, ready, accounts, language, other, adopt }: Options) {
	const renewals = useMemo(() => new Renewals(), []);
	const [renewed, setRenewed] = useState(0);
	const last = useRef<Identity>();
	const { session: renewal } = useSession({
		product: 'snapshots',
		person: ready && identity ? { id: identity.account, name: identity.name ?? null } : null,
		expires: identity?.session.expires ?? null,
		accounts,
		labels: language === 'es' ? Renewal.labels.es : {},
		read: async () => {
			try {
				const found = await session.identity();
				last.current = found;
				return { state: 'signed', person: { id: found.account, name: found.name ?? null }, expires: found.session.expires ?? null };
			} catch (cause) {
				// No session is an answer; anything else is the service not answering, which is never a sign-out
				if (cause instanceof Refusal && cause.lost) return { state: 'ended', reason: cause.reason };
				// Who did not answer, by its id: an authority the service named, or else the service itself
				if (cause instanceof Refusal && cause.unreachable) return { state: 'unavailable', source: Renewals.source(cause) };
				throw cause;
			}
		},
		start: mode => session.signin(Session.destination(globalThis.location.pathname, globalThis.location.search), mode),
		other,
		onrenewed: () => {
			if (last.current) adopt(last.current);
			renewals.notify();
			setRenewed(renewals.count);
		},
		onchanged: () => globalThis.location.assign('/')
	});
	useEffect(() => {
		client.guard = renewal ? lost => renewal.lost(lost) : null;
		return () => {
			client.guard = null;
		};
	}, [client, renewal]);
	const resumed = useCallback(() => renewals.next(), [renewals]);
	return { renewal, renewed, resumed };
}
